import { labeebBusinessRef, type LabeebTenantSummary } from "../../api/labeeb";
import { searchOnlyValue, type McpTenantSummary } from "../../api/mcp";

/**
 * One merchant, as both products see it.
 *
 * `mcp` is null for a merchant only Labeeb knows. That is the whole point of
 * this type: the list used to iterate MCP's response alone, so a merchant with
 * a Labeeb config item and no MCP history was ABSENT from the console — not
 * shown as inactive, absent — and nothing on the page could say so.
 */
export interface MergedTenant {
  reference: string;
  mcp: McpTenantSummary | null;
  labeeb: LabeebTenantSummary | null;
}

/**
 * Every merchant either product knows, once each.
 *
 * MCP's rows come first and keep their order — it is the larger list and the
 * one with names — then any Labeeb reference MCP has never seen.
 *
 * THE JOIN KEY is MCP's `business_reference` ↔ Labeeb's `tenant_id`.
 *
 * That is also the admin path key (`GET /admin/tenant/{tenant_id}`).
 * `profile.bi_business_id` is the analytics warehouse id and is a different
 * value — using it as the page address 404s every Labeeb panel.
 */
export function mergeTenants(
  mcp: McpTenantSummary[],
  labeeb: LabeebTenantSummary[],
): MergedTenant[] {
  const keyOf = (t: LabeebTenantSummary): string => labeebBusinessRef(t);

  const byRef = new Map<string, LabeebTenantSummary>();
  for (const t of labeeb) {
    const key = keyOf(t);
    if (key) byRef.set(key, t);
  }

  const seen = new Set<string>();
  const merged: MergedTenant[] = [];

  for (const m of mcp) {
    const reference = String(m.business_reference);
    seen.add(reference);
    merged.push({ reference, mcp: m, labeeb: byRef.get(reference) ?? null });
  }

  for (const t of labeeb) {
    const reference = keyOf(t);
    // A merchant present in both is already in the list with its MCP half.
    // Adding it again here is the bug this guard exists for — it would show
    // the same merchant twice, once without a name.
    if (!reference || seen.has(reference)) continue;
    seen.add(reference);
    merged.push({ reference, mcp: null, labeeb: t });
  }

  return merged;
}

/**
 * The merchant's name, from whichever product knows it, or null.
 *
 * MCP first because its list is the one with names for most of the fleet, then
 * Labeeb's — which the console used to drop entirely, rendering a Labeeb-only
 * merchant as an em dash and a "Labeeb only" badge under the belief that
 * Labeeb's list carried no name. It does: the admin API attaches it from the
 * BusinessProfile item.
 *
 * NULL rather than `""` when neither has one, so a caller renders a dash
 * deliberately instead of an empty cell that reads as a layout bug. Absence is
 * normal, not broken — the profile write is an observational side effect of
 * session resolution and its failures are swallowed.
 */
export function nameOf(t: MergedTenant): string | null {
  return t.mcp?.business_name || t.labeeb?.profile?.business_name || null;
}

/**
 * Text to match a filter against — every identifier the estate keys on.
 *
 * The same merchant is keyed four ways, and an operator arriving from any one
 * store holds whichever key that store uses (S0.1 / DA-3335):
 *
 *   business_reference   MCP blocklist — what ops actually say out loud
 *   business name        what a human searches for
 *   tenant_id            Labeeb's admin path key, and the page address
 *   bi_business_id       the warehouse id — searchable, never the link
 *   business_id          the entitlements table's UUID
 *
 * Owner email is matched too, since support arrives with one often enough.
 *
 * `business_id` is MATCHED HERE AND RENDERED NOWHERE. This is the single site
 * that unwraps a `SearchOnly` value, which is what makes the brand on that
 * type worth having — see the docstring in `api/mcp.ts`. Accepting a UUID
 * pasted from a DynamoDB console costs nothing; putting one on screen beside
 * a reference number invites copying the wrong one into a ticket.
 *
 * It used to read MCP's fields alone, so a Labeeb-only merchant was findable
 * by reference and nothing else.
 */
export function searchTextOf(t: MergedTenant): string {
  // Deduplicated and compacted. Labeeb's `tenant_id` IS the reference for
  // most merchants, so joining blindly repeated it and padded the string with
  // empty segments — harmless to `includes`, but it made the haystack a
  // misleading thing to assert on, and a test rightly caught it.
  const parts = [
    t.reference,
    nameOf(t) ?? "",
    t.mcp?.email ?? "",
    t.labeeb?.tenant_id ?? "",
    t.labeeb?.profile?.bi_business_id ?? "",
    searchOnlyValue(t.mcp?.business_id),
  ].filter((p) => p !== "");

  return [...new Set(parts)].join(" ").toLowerCase();
}

/** The stored MCP tier, or null — including for a merchant MCP has never seen. */
export function mcpTierOf(t: MergedTenant): string | null {
  return t.mcp?.tier ?? null;
}

/**
 * Is Labeeb suspended for this merchant?
 *
 * REPLACES `labeebTierOf`. The fleet list no longer carries a tier — it is
 * its own document per business (`GET /admin/tenant/{id}/quota`), and
 * fetching one per row to fill a column would be N requests to answer a
 * question nobody scans a list for. Standing IS on the row, and it is the
 * thing worth seeing at a glance.
 */
export function labeebSuspended(t: MergedTenant): boolean {
  return t.labeeb?.status === "suspended";
}
